import type {
  SatelliteWebLandlordGetLatestForContactsPageNoteDto,
  SatelliteWebLandlordGetLatestForContactsPageRequestDto,
  SatelliteWebLandlordGetLatestForContactsPageResponseDto,
} from '@zg-rentals/ts-satellite-client';
import {
  SatelliteConversationFolderTypeV2,
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import type { QueryClient } from '@zg-rentals/react-query5';
import { useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage } from '@zg-rentals/ts-satellite-client-react-query';

export type GetContactsPageConversationsQueryParamsArgs = {
  clientTimeZone: string;
  endDateMs: number;
  limit: number;
  listingAlias?: string;
  start: number;
  startDateMs: number;
};

/**
 * Builds the request params for getLatestConversationsForContactsPage so that
 * cache updates (e.g. from SendMessageModal, Notepad) use the same key as the
 * query in LeadsTable.
 */
export function getContactsPageConversationsQueryParams({
  clientTimeZone,
  endDateMs,
  limit,
  listingAlias,
  start,
  startDateMs,
}: GetContactsPageConversationsQueryParamsArgs): SatelliteWebLandlordGetLatestForContactsPageRequestDto {
  return {
    clientTimeZone,
    startDateMs,
    endDateMs,
    folder: SatelliteConversationFolderTypeV2.INBOX,
    limit,
    start,
    ...(listingAlias && { listingAlias }),
    readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL,
    renterProgressStatus: SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL,
  };
}

/**
 * Callback to update a conversation's note in the contacts page React Query cache.
 * Used when Notepad saves/deletes a note or toggles application sent from the contacts page
 * so the leads table reflects changes without refetching.
 */
export type OnContactsPageConversationNoteUpdate = (
  conversationId: string,
  noteUpdate: Partial<SatelliteWebLandlordGetLatestForContactsPageNoteDto>,
) => void;

/**
 * Applies a persisted note change to the contacts page cache. Cancels any fetch already in flight
 * on the same key first - otherwise it resolves after this write and reverts it, which the leads
 * table is exposed to now that its query can refetch on focus. Mirrors patchConversationMessagesData.
 */
export async function patchContactsPageConversationNote(
  queryClient: QueryClient,
  queryParams: SatelliteWebLandlordGetLatestForContactsPageRequestDto,
  conversationId: string,
  noteUpdate: Partial<SatelliteWebLandlordGetLatestForContactsPageNoteDto>,
): Promise<void> {
  const queryKey = useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage.getKey(queryParams);

  await queryClient.cancelQueries({ queryKey });

  queryClient.setQueryData<SatelliteWebLandlordGetLatestForContactsPageResponseDto>(queryKey, (oldData) => {
    if (!oldData) return oldData;
    return {
      ...oldData,
      conversations: oldData.conversations?.map((conv) =>
        conv.conversationId === conversationId ? { ...conv, note: { ...conv.note, ...noteUpdate } } : conv,
      ),
    };
  });
}
